import type { HubHealth } from '@dyspatch/core';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, CalendarClock, Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useSession } from '@/components/auth/session';
import { Button } from '@/components/ui/button';
import { NewBriefButton } from '@/features/briefs/BriefComposer';
import { useWorkers } from '@/hooks/use-fleet';
import type { GateCounts } from '@/lib/fleet';
import { presenceOf } from '@/lib/fleet';
import { duration } from '@/lib/format';

/**
 * The one loud thing in the app, and the first thing a person reads after
 * signing in (issue #92).
 *
 * The panel this replaced opened on a 60px zero: the count of things waiting on
 * a person, drawn at full size whether that count was four or none. On a
 * healthy hub it is none most days, which made the largest type on the first
 * screen of the product a report of an absence, while the one thing a person
 * could actually do -- New job -- sat off to the side as the smallest control
 * in the band. A new person signing in for the first time got the same zero,
 * with nothing telling them what the product was for.
 *
 * So the hero now answers three questions in order, and which one leads
 * depends on whether anything is actually waiting:
 *
 *  - Nothing waiting: a greeting by name, one line on what the hub is for, and
 *    the actions a person can take from here -- file a job, schedule recurring
 *    work, enroll a laptop if none is online. "Nothing is waiting on you" is
 *    still said, but as a line in the band underneath, not as the hero's
 *    figure. There is no full-size zero anywhere in this state.
 *  - Something waiting: the count still leads at 60px, because a number that
 *    means "act now" is the one loud moment PRODUCT.md allows, and the
 *    greeting shrinks to a line above it instead.
 *
 * The two states share one surface and one aria-label ("What is waiting on
 * you") because they are the same band, ordered by whether the person is
 * needed, not two different panels.
 */
function Figure({ value }: { value: number }) {
  const previous = useRef(value);
  const [key, setKey] = useState(0);

  useEffect(() => {
    if (previous.current !== value) {
      previous.current = value;
      setKey((n) => n + 1);
    }
  }, [value]);

  return (
    <span
      key={key}
      className="dy-figure dy-tick block text-[3.75rem] leading-none"
      style={{ color: 'var(--dy-gate)' }}
    >
      {value}
    </span>
  );
}

/**
 * Morning, afternoon or evening, from the reader's own clock.
 *
 * Its own function, rather than inlined where the greeting is built, so the
 * boundary is one pinned decision instead of a copy of it at every call site
 * (`packages/hub/test/gate-hero-copy.test.ts`).
 */
export function timeOfDay(hour: number): 'morning' | 'afternoon' | 'evening' {
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

/** "Good morning", "Good afternoon", "Good evening", from the hour of day. */
export function timeGreeting(hour: number): string {
  return `Good ${timeOfDay(hour)}`;
}

/**
 * The one name a greeting says, from whatever the session offers.
 *
 * A session with a matched `HubUser` has a real name; Phase A auth otherwise
 * reports the email itself as `actor` (`packages/hub/src/auth.ts`), so this
 * takes the local part of an email and title-cases it rather than greeting
 * someone by their whole address. The empty and unparseable cases fall back
 * rather than throw, because a greeting is decoration and a broken one should
 * never be the reason the dashboard fails to render.
 */
export function firstName(display: string): string {
  const trimmed = display.trim();
  if (!trimmed) return 'there';
  const local = trimmed.includes('@') ? (trimmed.split('@')[0] ?? trimmed) : trimmed;
  const [first] = local.split(/[\s._-]+/).filter(Boolean);
  if (!first) return trimmed;
  return first.charAt(0).toUpperCase() + first.slice(1);
}

/**
 * The empty state's own words, pinned so a future edit cannot put a numeral
 * back where the queue is meant to be described as clear.
 *
 * `started` tells a clear queue apart from a hub nobody has ever filed
 * anything on -- the same number, a completely different sentence. Both
 * versions are plain prose, checked by the pinning test never to contain a
 * digit.
 */
export function nothingWaitingCopy(started: boolean): string {
  return started
    ? 'Nothing is waiting on you. Every plan is approved and every pull request is reviewed.'
    : 'Nothing has been filed here yet.';
}

export function GateHero({
  gates,
  oldestMinutes,
  health,
  /**
   * Whether this hub has ever had a job filed on it.
   *
   * A clear queue and an empty hub are the same number and a completely
   * different sentence. "Every plan is approved and every pull request is
   * reviewed" is a reassurance on a working hub and a small lie on a fresh one,
   * where there has never been a plan; the checklist below is the honest answer
   * there and this line should not talk over it.
   */
  started = true,
}: {
  gates: GateCounts;
  oldestMinutes: number;
  health?: HubHealth;
  started?: boolean;
}) {
  const { session } = useSession();
  const warnings = health?.warnings?.length ?? 0;

  // Enrolling is an admin-only verb (`EnrollButton` on `/agents` is the same
  // gate), so this only ever fetches the fleet for an admin -- a member would
  // just earn a 403 for a link they cannot use. `workers.data` staying
  // `undefined` until the query resolves means the link is briefly absent
  // rather than briefly wrong, which is the harmless direction to fail.
  const workers = useWorkers(session.isAdmin);
  const anyOnline = (workers.data ?? []).some((worker) => presenceOf(worker) !== 'offline');
  const showEnroll = session.isAdmin && workers.data !== undefined && !anyOnline;

  const waiting = gates.total > 0;
  const greeting = `${timeGreeting(new Date().getHours())}, ${firstName(session.user?.name || session.actor)}`;

  // The gate breakdown, only naming the halves that exist. "2 plans · 0 pull
  // requests" spends a clause on a zero; the zero is already in the total.
  const parts: string[] = [];
  if (gates.plans) parts.push(`${gates.plans} plan${gates.plans === 1 ? '' : 's'} to approve`);
  if (gates.prs) parts.push(`${gates.prs} pull request${gates.prs === 1 ? '' : 's'} to review`);

  const secondaryActions = (
    <>
      <Button asChild variant="outline" size="sm">
        <Link
          to="/schedules"
          title="Set up a job that files itself: a PR review pass, a docs pass, or anything else on a clock."
        >
          <CalendarClock aria-hidden />
          Schedule recurring work
        </Link>
      </Button>
      {showEnroll ? (
        <Button asChild variant="outline" size="sm">
          <Link to="/agents" title="Nothing can run until a laptop is online to run it.">
            <Plus aria-hidden />
            Enroll a device
          </Link>
        </Button>
      ) : null}
    </>
  );

  if (!waiting) {
    return (
      <section
        className="dy-surface-2 px-5 py-5 sm:px-7 sm:py-6"
        aria-label="What is waiting on you"
      >
        <div className="flex flex-col gap-y-5 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between sm:gap-x-8">
          <div className="min-w-0">
            <p className="text-foreground text-2xl leading-tight font-semibold">{greeting}</p>
            <p className="text-fg-2 mt-1 text-base">
              File a job, an agent drafts the plan, you approve it, and the pull request it opens
              comes back here for you to review.
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <NewBriefButton tone="hero" />
            {secondaryActions}
          </div>
        </div>

        {/* The status band: what used to be the hero's own figure is a line
            here instead, underneath the actions rather than above them. */}
        <p className="text-fg-3 mt-5 text-sm">{nothingWaitingCopy(started)}</p>
        {warnings && session.isAdmin ? (
          <p className="mt-1 text-sm">
            <Link
              to="/admin"
              className="inline-flex items-center gap-1 underline-offset-2 transition-opacity duration-100 hover:opacity-80 hover:underline"
              style={{ color: 'var(--dy-warn)' }}
            >
              <AlertTriangle className="size-3.5" aria-hidden />
              {warnings} configuration warning{warnings === 1 ? '' : 's'}
            </Link>
          </p>
        ) : null}
      </section>
    );
  }

  return (
    <section className="dy-surface-2 px-5 py-5 sm:px-7 sm:py-6" aria-label="What is waiting on you">
      {/* Stacked on a phone, with the button under the sentence rather than
          above it: the figure is what somebody came here to read first. */}
      <div className="flex flex-col gap-y-5 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between sm:gap-x-8">
        <div className="min-w-0 flex flex-col gap-y-3 sm:flex-row sm:flex-wrap sm:items-end sm:gap-x-8">
          <div className="min-w-0">
            <p className="text-fg-3 text-sm">{greeting}</p>
            <Figure value={gates.total} />
            <p className="text-foreground mt-2 text-lg font-medium">waiting on you</p>
          </div>

          <div className="min-w-0 flex-1 sm:pb-1.5">
            <p className="text-fg-2 text-base">
              {parts.join(' · ')}
              {oldestMinutes > 0 ? (
                <>
                  {'. The oldest has waited '}
                  <span className="text-foreground tnum font-medium">
                    {duration(oldestMinutes)}
                  </span>
                  .
                </>
              ) : (
                '.'
              )}
            </p>
            {warnings && session.isAdmin ? (
              <p className="mt-1 text-sm">
                <Link
                  to="/admin"
                  className="inline-flex items-center gap-1 underline-offset-2 transition-opacity duration-100 hover:opacity-80 hover:underline"
                  style={{ color: 'var(--dy-warn)' }}
                >
                  <AlertTriangle className="size-3.5" aria-hidden />
                  {warnings} configuration warning{warnings === 1 ? '' : 's'}
                </Link>
              </p>
            ) : null}
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <NewBriefButton tone="hero" />
          {secondaryActions}
        </div>
      </div>
    </section>
  );
}

/** Shared by the hero and the compact header line, so they cannot disagree. */
export function gateSentence(gates: GateCounts): string {
  if (!gates.total) return 'nothing waiting';
  return `${gates.total} waiting on you`;
}
